import { IProductTemplateTableEditor, OptionalFilter } from "components/ComponentTypeTypes";
import { DatasetChartProps } from "components/dashboard/charts/interfaces";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitchChangeEvent } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import React from "react";
import { T } from "services/lang/LanguageService";
import { handleRowReorderEvt } from "utils/utils";

interface ProductTemplateFilterConfigProps {
  currentConfig: IProductTemplateTableEditor | DatasetChartProps;
  setCurrentConfig: React.Dispatch<React.SetStateAction<IProductTemplateTableEditor | DatasetChartProps>>;
  handleConfigChange: (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent | InputSwitchChangeEvent | MultiSelectChangeEvent
  ) => void;
}

const ProductTemplateFilterConfig = ({
  currentConfig,
  setCurrentConfig,
  handleConfigChange,
}: ProductTemplateFilterConfigProps) => {
  const textInputTemplate = (options: ColumnEditorOptions) => {
    return <InputText value={options.value} onChange={e => options.editorCallback?.(e.target.value)} />;
  };

  const removeButtonTemplate = (rowData: OptionalFilter) => {
    return (
      <Button
        icon="pi pi-times"
        className="p-button-rounded p-button-text p-button-danger"
        onClick={() => removeFilter(rowData)}
      />
    );
  };

  const addButtonTemplate = () => {
    return <Button icon="pi pi-plus" className="p-button-text p-button-success" onClick={() => addFilter()} />;
  };

  const addFilter = () => {
    const optionalFilters = [...(currentConfig.optionalFilters ?? [])];
    optionalFilters.push({ label: "", filter: "" });
    setCurrentConfig({ ...currentConfig, optionalFilters });
  };

  const removeFilter = (rowData: OptionalFilter) => {
    const optionalFilters = [...(currentConfig.optionalFilters ?? [])];
    optionalFilters.splice(optionalFilters.indexOf(rowData), 1);
    setCurrentConfig({ ...currentConfig, optionalFilters });
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent): void => {
    const { newData, index } = e;
    const optionalFilters = [...(currentConfig.optionalFilters ?? [])];
    optionalFilters[index] = { ...optionalFilters[index], ...newData };
    setCurrentConfig({ ...currentConfig, optionalFilters });
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-row gap-2">
        <div className="flex flex-column gap-2">
          <label htmlFor="filter">{T("word.hidden-filter")}</label>
          <InputText id="filter" value={currentConfig?.filter ?? ""} onChange={handleConfigChange} />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="defaultFilter">{T("word.default-filter")}</label>
          <InputText id="defaultFilter" value={currentConfig?.defaultFilter ?? ""} onChange={handleConfigChange} />
        </div>
      </div>
      <div className="flex flex-column gap-2">
        <label htmlFor="optionalFilters">{T("label.optional-filters")}</label>
        <DataTable
          value={currentConfig?.optionalFilters ?? []}
          onRowEditComplete={onRowEditComplete}
          editMode="row"
          onRowReorder={handleRowReorderEvt(currentConfig.optionalFilters, setCurrentConfig, currentConfig, "optionalFilters")}
          reorderableRows
          size="small"
        >
          <Column field="label" header={T("word.label")} style={{ width: "40%" }} editor={textInputTemplate} />
          <Column field="filter" header={T("word.filter")} style={{ width: "45%" }} editor={textInputTemplate} />
          <Column style={{ width: "10%", textAlign: "end" }} headerStyle={{ width: "10%", textAlign: "end" }} rowEditor />
          <Column style={{ width: "5%" }} header={addButtonTemplate} body={removeButtonTemplate} />
        </DataTable>
      </div>
    </div>
  );
};

export default ProductTemplateFilterConfig;
